import { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { getSubscriptions, getMemberSubscription, subscribe, cancelSubscription } from '../../api';
import './Abonnements.css';

function Abonnements() {
  const { token } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [subsResponse, memberSubResponse] = await Promise.all([
        getSubscriptions(),
        getMemberSubscription(token)
      ]);
      setSubscriptions(subsResponse.data);
      if (memberSubResponse.data) {
        setCurrentSubscription(memberSubResponse.data);
      }
    } catch (err) {
      setError('Impossible de charger les abonnements');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (subscription) => {
    setSelectedSubscription(subscription);
    setSubmitError('');
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      // Si on a déjà un abonnement, on l'annule d'abord
      if (currentSubscription) {
        await cancelSubscription(token);
      }

      const response = await subscribe(token, selectedSubscription.id);
      setCurrentSubscription(response.data);
      setShowConfirmModal(false);
      setSuccessMessage(
        currentSubscription
          ? `Votre abonnement a été changé pour ${selectedSubscription.name}.`
          : `Votre abonnement ${selectedSubscription.name} a été activé avec succès.`
      );
      setShowSuccessModal(true);
    } catch (err) {
      setSubmitError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      await cancelSubscription(token);
      const cancelledName = currentSubscription?.subscription?.name;
      setCurrentSubscription(null);
      setShowCancelModal(false);
      setSuccessMessage(`Votre abonnement ${cancelledName} a été annulé.`);
      setShowSuccessModal(true);
    } catch (err) {
      setSubmitError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedSubscription(null);
    setSubmitError('');
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSubmitError('');
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
  };

  const isCurrentSubscription = (sub) => {
    return currentSubscription?.subscription?.id === sub.id;
  };

  const isChangingSubscription = currentSubscription && selectedSubscription && !isCurrentSubscription(selectedSubscription);

  return (
    <div className="abonnements-page">
      <header className="abonnements-header">
        <h1>Abonnements</h1>
      </header>

      <main className="abonnements-content">
        <p className="abonnements-intro">Choisissez l'abonnement qui vous convient</p>

        {currentSubscription && (
          <div className="current-subscription-banner">
            <div className="current-subscription-info">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span>Abonnement actuel : <strong>{currentSubscription.subscription?.name}</strong> - {currentSubscription.subscription?.price} €/mois</span>
            </div>
            <button className="cancel-subscription-btn" onClick={() => setShowCancelModal(true)}>
              Annuler l'abonnement
            </button>
          </div>
        )}

        {loading && <p className="abonnements-loading">Chargement...</p>}

        {error && <p className="abonnements-error">{error}</p>}

        {!loading && !error && (
          <div className="abonnements-grid">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className={`abonnement-card ${isCurrentSubscription(sub) ? 'active' : ''}`}
              >
                {isCurrentSubscription(sub) && (
                  <div className="abonnement-badge">Actif</div>
                )}
                <h3>{sub.name}</h3>
                <p className="abonnement-type">{sub.type}</p>
                <p className="abonnement-price">
                  {sub.price} €<span className="price-period">/mois</span>
                </p>
                <p className="abonnement-description">{sub.description}</p>
                <button
                  onClick={() => handleSelect(sub)}
                  className="abonnement-button"
                  disabled={isCurrentSubscription(sub)}
                >
                  {isCurrentSubscription(sub) ? 'Abonnement actuel' : currentSubscription ? 'Changer' : 'Choisir'}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && subscriptions.length === 0 && (
          <p className="abonnements-empty">Aucun abonnement disponible pour le moment.</p>
        )}
      </main>

      {/* Modal de confirmation d'abonnement/changement */}
      {showConfirmModal && selectedSubscription && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isChangingSubscription ? "Changer d'abonnement" : "Confirmer l'abonnement"}</h2>
              <button className="modal-close" onClick={closeConfirmModal}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {submitError && (
                <div className="modal-error">{submitError}</div>
              )}

              {isChangingSubscription && (
                <div className="change-subscription-notice">
                  <div className="change-from">
                    <span className="change-label">Abonnement actuel</span>
                    <span className="change-name">{currentSubscription.subscription?.name}</span>
                    <span className="change-price">{currentSubscription.subscription?.price} €/mois</span>
                  </div>
                  <div className="change-arrow">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"/>
                    </svg>
                  </div>
                  <div className="change-to">
                    <span className="change-label">Nouvel abonnement</span>
                    <span className="change-name">{selectedSubscription.name}</span>
                    <span className="change-price">{selectedSubscription.price} €/mois</span>
                  </div>
                </div>
              )}

              {!isChangingSubscription && (
                <div className="confirm-details">
                  <div className="confirm-subscription-name">{selectedSubscription.name}</div>
                  <div className="confirm-subscription-price">
                    {selectedSubscription.price} € <span>/mois</span>
                  </div>
                  <p className="confirm-subscription-desc">{selectedSubscription.description}</p>
                </div>
              )}

              <div className="demo-notice">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>Mode démo : aucun paiement ne sera effectué</span>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeConfirmModal} disabled={submitting}>
                Annuler
              </button>
              <button type="button" className="btn-primary" onClick={handleConfirm} disabled={submitting}>
                {submitting ? 'Confirmation...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation d'annulation */}
      {showCancelModal && (
        <div className="modal-overlay" onClick={closeCancelModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Annuler l'abonnement</h2>
              <button className="modal-close" onClick={closeCancelModal}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {submitError && (
                <div className="modal-error">{submitError}</div>
              )}
              <div className="cancel-details">
                <svg viewBox="0 0 24 24" fill="currentColor" className="cancel-icon">
                  <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                </svg>
                <p>Êtes-vous sûr de vouloir annuler votre abonnement <strong>{currentSubscription?.subscription?.name}</strong> ?</p>
              </div>
              <div className="demo-notice">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>Mode démo : aucune facturation n'est active</span>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeCancelModal} disabled={submitting}>
                Garder l'abonnement
              </button>
              <button type="button" className="btn-danger" onClick={handleCancelSubscription} disabled={submitting}>
                {submitting ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={closeSuccessModal}>
          <div className="modal-content success-modal" onClick={e => e.stopPropagation()}>
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h2>Succès !</h2>
            <p>{successMessage}</p>
            <p className="success-demo-note">
              (Ceci est une simulation - aucun paiement n'a été effectué)
            </p>
            <button className="btn-primary" onClick={closeSuccessModal}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Abonnements;
